import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/shadcn/button'
import { ConditionPickerDialog, type ConditionGroup } from './ConditionPickerDialog'
import { areConditionsLocked, subtypeLabel } from './condition-lib'
import type { PropertyEditorProps } from '@/lib/action-modules/registry/subtypes/types'

/** Picks another subtype this one depends on (`conditionSubtypeId`). */
export function ConditionSubtypeEditor({ value, onChanged, subtype, allSubtypes }: PropertyEditorProps) {
  const { t } = useTranslation('action-modules-wizard')
  const [open, setOpen] = useState(false)

  if (areConditionsLocked(allSubtypes)) {
    return <p className="text-sm text-muted-foreground">{t('tabSubtypes.condition.saveFirst')}</p>
  }

  const selectedId = typeof value === 'number' ? value : null

  // Persisted siblings (excluding self) are the only valid condition sources.
  const candidates = allSubtypes.filter(s => s.editorId !== subtype.editorId && s.subtype.id != null)
  const groups: ConditionGroup[] = [
    { key: 'subtypes', options: candidates.map(s => ({ id: s.subtype.id as number, label: subtypeLabel(s, t) })) },
  ]

  const selected = candidates.find(s => s.subtype.id === selectedId)
  const triggerLabel =
    selectedId === null
      ? t('tabSubtypes.condition.none')
      : selected
        ? subtypeLabel(selected, t)
        : t('tabSubtypes.condition.unknown', { id: selectedId })

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="w-full justify-start font-normal"
        onClick={() => setOpen(true)}
      >
        <span className="truncate">{triggerLabel}</span>
      </Button>
      <ConditionPickerDialog
        open={open}
        onOpenChange={setOpen}
        title={t('tabSubtypes.condition.subtypeTitle')}
        groups={groups}
        selectedId={selectedId}
        onSelect={id => onChanged(id)}
      />
    </>
  )
}
